import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Eye,
  Package,
  MapPin,
  Tag,
  AlertTriangle,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { apiService, api } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { ErrorHandler } from '../../utils/errorHandler';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import JsBarcode from 'jsbarcode';
import * as XLSX from 'xlsx';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDateTime } from '../../utils/formatDateTime';
import Button from '../../components/UI/Button';
import { DeletionProtectionModal } from '../../components/UI';
import { useDeletionProtection } from '../../hooks/useDeletionProtection';

interface Product {
  _id: string;
  name: string;
  sku: string;
  barcode?: string;
  description?: string;
  category: {
    _id: string;
    name: string;
  };
  location: {
    _id: string;
    name: string;
  };
  price: number;
  costPrice: number;
  stockQuantity: number;
  minStockLevel: number;
  unit: string;
  units?: string[];
  supplier?: string | { name: string };
  image?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  customFields?: {
    model?: string;
    brand?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    type?: string;
    compressorType?: string;
    specification?: string;
    details?: string;
    serialNo?: string;
    partNo?: string;
    capacity?: string;
    description?: string;
    notes?: string;
    comments?: string;
    generatedBarcode?: string;
  };
}

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [rootCategories, setRootCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState<string>('');
  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('');
  const [createModalData, setCreateModalData] = useState({ name: '', description: '', website: '', code: '', region: '' });
  
  // Add view and edit product state
  const [showViewProductModal, setShowViewProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState({
    productName: '',
    model: '',
    brand: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    type: '',
    compressorType: '',
    specification: '',
    details: '',
    serialNo: '',
    partNo: '',
    barcodeNo: '',
    generatedBarcode: '',
    sku: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    unit: '',
    unitIds: [] as string[],
    description: '',
    image: '',
    category: '',
    location: '',
    status: 'active',
  });
  const [addProductUnitsDropdownOpen, setAddProductUnitsDropdownOpen] = useState(false);
  const [editProductUnitsDropdownOpen, setEditProductUnitsDropdownOpen] = useState(false);
  const addUnitsDropdownRef = useRef<HTMLDivElement>(null);
  const editUnitsDropdownRef = useRef<HTMLDivElement>(null);

  // Deletion protection
  const {
    showDeletionProtectionModal,
    deletionProtectionData,
    checkAndDelete,
    closeDeletionProtectionModal
  } = useDeletionProtection();
  const [newProduct, setNewProduct] = useState<{
    productName: string; model: string; brand: string; countryOfOrigin: string; countryOfAssembly: string; type: string; compressorType: string; specification: string; details: string; serialNo: string; partNo: string; barcodeNo: string; generatedBarcode: string; sku: string; price: string; costPrice: string; stockQuantity: string; minStockLevel: string; unit: string; unitId: string; unitIds: string[]; description: string; image: string; category: string; location: string; status: string;
  }>({
    productName: '',
    model: '',
    brand: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    type: '',
    compressorType: '',
    specification: '',
    details: '',
    serialNo: '',
    partNo: '',
    barcodeNo: '',
    generatedBarcode: '',
    sku: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    unit: '',
    unitId: '',
    unitIds: [] as string[],
    description: '',
    image: '',
    category: '',
    location: '',
    status: 'active',
  });
  const [barcodeImage, setBarcodeImage] = useState<string | null>(null);
  const [editBarcodeImage, setEditBarcodeImage] = useState<string | null>(null);
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const viewBarcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const editBarcodeCanvasRef = useRef<HTMLCanvasElement>(null);

  // Add state for view barcode image
  const [viewBarcodeImage, setViewBarcodeImage] = useState<string | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importedProducts, setImportedProducts] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  // Add state for the import file
  const [importFile, setImportFile] = useState<File | null>(null);
  // Add state for import errors and modal
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [showImportErrorsModal, setShowImportErrorsModal] = useState(false);
  // Add state for header errors
  const [headerError, setHeaderError] = useState<{ missing: string[]; extra: string[] } | null>(null);
  const [showHeaderErrorModal, setShowHeaderErrorModal] = useState(false);

  const { settings } = useSettings();
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';
  const barcodeFormat = settings?.barcode?.format || 'CODE128';
  const barcodeWidth = settings?.barcode?.width || 2;
  const barcodeHeight = settings?.barcode?.height || 100;
  const barcodeIncludeText = settings?.barcode?.includeText !== undefined ? settings.barcode.includeText : true;

  // Add URL parameter handling for search and location state for add modal
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Handle URL search parameter and location state for add modal
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
    
    // Check if we should open the add modal from location state
    if (location.state?.openAddModal) {
      setShowAddProductModal(true);
      // Clear the state to prevent reopening modal on refresh
      window.history.replaceState({}, document.title);
    }
  }, [searchParams, setSearchTerm, location.state]);

  const validateFile = (file: File) => {
    const maxFileSize = settings?.system?.maxFileSize || 0;
    const allowedFileTypes = settings?.system?.allowedFileTypes || [];
    if (maxFileSize && file.size > maxFileSize) {
      toast.error(`File size exceeds the maximum allowed (${(maxFileSize/1024/1024).toFixed(2)} MB)`);
      return false;
    }
    if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(file.type)) {
      toast.error('File type not allowed.');
      return false;
    }
    return true;
  };

  // Function to generate barcode image
  const generateBarcodeImage = (barcodeText: string) => {
    if (!barcodeText || !barcodeCanvasRef.current) return;
    try {
      const canvas = barcodeCanvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      JsBarcode(canvas, barcodeText, {
        format: barcodeFormat,
        width: barcodeWidth,
        height: barcodeHeight,
        displayValue: barcodeIncludeText,
        fontSize: 16,
        margin: 10,
        background: "#ffffff",
        lineColor: "#000000"
      });
      const dataURL = canvas.toDataURL('image/png');
      setBarcodeImage(dataURL);
    } catch (error) {
      console.error('Error generating barcode image:', error);
    }
  };

  // Function to download barcode image
  const downloadBarcodeImage = () => {
    if (!barcodeImage || !newProduct.generatedBarcode) return;

    const link = document.createElement('a');
    link.download = `barcode-${newProduct.generatedBarcode}.png`;
    link.href = barcodeImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to generate barcode image for existing products
  const generateBarcodeImageForProduct = (barcodeText: string, canvasRef: React.RefObject<HTMLCanvasElement>): string | undefined => {
    if (!barcodeText || !canvasRef.current) return undefined;
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return undefined;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      JsBarcode(canvas, barcodeText, {
        format: barcodeFormat,
        width: barcodeWidth,
        height: barcodeHeight,
        displayValue: barcodeIncludeText,
        fontSize: 14,
        margin: 8,
        background: "#ffffff",
        lineColor: "#000000"
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating barcode image for product:', error);
      return undefined;
    }
  };

  // Function to download barcode image for existing products
  const downloadProductBarcodeImage = (barcodeText: string, canvasRef: React.RefObject<HTMLCanvasElement>) => {
    const barcodeImageData = generateBarcodeImageForProduct(barcodeText, canvasRef);
    if (!barcodeImageData) return;

    const link = document.createElement('a');
    link.download = `barcode-${barcodeText}.png`;
    link.href = barcodeImageData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to generate barcode based on selected fields
  const generateBarcode = async (productName: string, model: string, brand: string, specification: string, locationId?: string) => {
    if (!productName) {
      return '';
    }

    // Get the actual names from the selected IDs if available
    let brandName = '';
    if (brand) {
      const selectedBrand = brands.find(b => b._id === brand);
      brandName = selectedBrand ? selectedBrand.name : brand;
    }

    // Get location name if locationId is provided
    let locationName = '';
    if (locationId) {
      const selectedLocation = locations.find(l => l._id === locationId);
      locationName = selectedLocation ? selectedLocation.name : '';
    }

    // 1st letter from Product Name
    const productLetter = productName.charAt(0).toUpperCase();
    // Last 2 letters from Model (if available)
    const modelLetters = model ? model.slice(-2).toUpperCase() : '';
    // 1st letter from Brand (if available)
    const brandLetter = brandName ? brandName.charAt(0).toUpperCase() : '';
    // 1st letter from Specification (if available)
    const specLetter = specification ? specification.charAt(0).toUpperCase() : '';
    
    // Location letters: 1st, 6th, and last letter (if available)
    let locationLetters = '';
    if (locationName && locationName.length > 0) {
      const firstLetter = locationName.charAt(0).toUpperCase();
      const sixthLetter = locationName.length >= 6 ? locationName.charAt(5).toUpperCase() : '';
      const lastLetter = locationName.charAt(locationName.length - 1).toUpperCase();
      locationLetters = `${firstLetter}${sixthLetter}${lastLetter}`;
    }
    
    // Random 3 digit number
    const randomDigits = Math.floor(100 + Math.random() * 900).toString();

    let baseBarcode = `${productLetter}${modelLetters}${brandLetter}${specLetter}${locationLetters}${randomDigits}`;
    baseBarcode = baseBarcode.replace(/[^A-Z0-9]/g, '').toUpperCase();

    // Optionally, check for uniqueness (if needed)
    // try {
    //   const response = await apiService.get(`/products/check-barcode/${baseBarcode}`);
    //   const existingCount = response.data.count || 0;
    //   if (existingCount > 0) {
    //     baseBarcode = `${baseBarcode}${(existingCount + 1).toString().padStart(2, '0')}`;
    //   }
    //   return baseBarcode;
    // } catch (error) {
    //   const timestamp = Date.now().toString().slice(-4);
    //   return `${baseBarcode}${timestamp}`;
    // }

    return baseBarcode;
  };

  // Function to update barcode when fields change
  const updateBarcode = async (updatedProduct: any) => {
    const { brand, model, type, compressorType, location } = updatedProduct;
    
    if (brand && model && type && compressorType) {
      try {
        const generatedBarcode = await generateBarcode(updatedProduct.name, model, type, compressorType, location);
        setNewProduct(prev => ({
          ...prev,
          ...updatedProduct,
          unitId: prev.unitId,
          unitIds: prev.unitIds ?? [],
          generatedBarcode,
          barcodeNo: generatedBarcode // Also update the barcode field
        }));
        
        // Generate barcode image
        if (generatedBarcode) {
          generateBarcodeImage(generatedBarcode);
        }
      } catch (error) {
        console.error('Error generating barcode:', error);
        ErrorHandler.handleOperationError('generate', error as any);
      }
    } else {
      setNewProduct(prev => ({
        ...prev,
        ...updatedProduct,
        unitId: prev.unitId,
        unitIds: prev.unitIds ?? [],
        generatedBarcode: '',
        barcodeNo: ''
      }));
      setBarcodeImage(null);
    }
  };

  // Function to update barcode in edit modal when fields change
  const updateEditBarcode = async (updatedProduct: any) => {
    const { brand, model, type, compressorType, location } = updatedProduct;
    if (brand && model && type && compressorType) {
      try {
        const generatedBarcode = await generateBarcode(updatedProduct.name, model, type, compressorType, location);
        setEditProduct((prev: any) => ({
          ...prev,
          ...updatedProduct,
          generatedBarcode,
          barcodeNo: generatedBarcode
        }));
        // Generate barcode image
        if (generatedBarcode && editBarcodeCanvasRef.current) {
          try {
            const canvas = editBarcodeCanvasRef.current;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              JsBarcode(canvas, generatedBarcode, {
                format: barcodeFormat,
                width: barcodeWidth,
                height: barcodeHeight,
                displayValue: barcodeIncludeText,
                fontSize: 14,
                margin: 8,
                background: "#ffffff",
                lineColor: "#000000"
              });
              setEditBarcodeImage(canvas.toDataURL('image/png'));
            }
          } catch (err) {
            setEditBarcodeImage(null);
          }
        }
      } catch (error) {
        setEditBarcodeImage(null);
        ErrorHandler.handleOperationError('generate', error as any);
      }
    } else {
      setEditProduct((prev: any) => ({
        ...prev,
        ...updatedProduct,
        generatedBarcode: '',
        barcodeNo: ''
      }));
      setEditBarcodeImage(null);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Clear cache on component mount to ensure fresh data
        apiService.clearCache();
        
        // Use our modified fetchProducts function to get all products
        await fetchProducts(true);
        
        // Use batch request for other data
        const [
          categoriesRes,
          rootCategoriesRes,
          locationsRes,
          brandsRes,
          typesRes,
          compressorTypesRes,
          countriesRes,
          unitsRes
        ] = await apiService.batchGet([
          '/categories/all',
          '/categories/root',
          '/locations/all',
          '/brands/all',
          '/product-types/all',
          '/compressor-types/all',
          '/countries/all',
          '/units/all'
        ]);

        setCategories(categoriesRes.data);
        setRootCategories(rootCategoriesRes.data || []);
        setLocations(locationsRes.data || []);
        setBrands(brandsRes.data || []);
        setTypes(typesRes.data || []);
        setCompressorTypes(compressorTypesRes.data || []);
        setCountries(countriesRes.data || []);
        setUnits(unitsRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        ErrorHandler.handleOperationError('fetch', error as any);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    console.log('Brands state changed:', brands);
  }, [brands]);

  // Update edit product when units are loaded and edit modal is open (multi-unit support)
  useEffect(() => {
    if (showEditProductModal && selectedProduct && units.length > 0) {
      const productUnits = (selectedProduct as any).units;
      const unitIds: string[] = Array.isArray(productUnits) && productUnits.length > 0
        ? productUnits
            .map((name: string) => units.find(u => u.name === name)?._id)
            .filter(Boolean) as string[]
        : (() => {
            const u = units.find(unit => unit.name === selectedProduct.unit);
            return u ? [u._id] : [];
          })();
      setEditProduct(prev => ({
        ...prev,
        unit: unitIds[0] || '',
        unitIds: unitIds.length ? unitIds : prev.unitIds
      }));
    }
  }, [units, showEditProductModal, selectedProduct]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addUnitsDropdownRef.current && !addUnitsDropdownRef.current.contains(e.target as Node)) {
        setAddProductUnitsDropdownOpen(false);
      }
      if (editUnitsDropdownRef.current && !editUnitsDropdownRef.current.contains(e.target as Node)) {
        setEditProductUnitsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProducts = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // Clear all cache to ensure fresh data
      apiService.clearCache();
      
      let allProducts: Product[] = [];
      let page = 1;
      let hasMorePages = true;
      const limit = 1000; // Fetch 1000 products per request
      
      console.log('Fetching all products with pagination...');
      
      // Fetch all products using pagination
      while (hasMorePages) {
        const url = `/products?page=${page}&limit=${limit}&_t=${Date.now()}`;
        console.log(`Fetching page ${page} with URL:`, url);
        
        // Use axios directly to bypass all caching mechanisms
        const response = await api.get(url, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        
        const { products, total, hasNextPage } = response.data;
        
        console.log(`Page ${page}: Fetched ${products?.length || 0} products`);
        console.log(`Total products in database: ${total}`);
        console.log(`Has next page: ${hasNextPage}`);
        
        if (products && products.length > 0) {
          allProducts = [...allProducts, ...products];
        }
        
        hasMorePages = hasNextPage;
        page++;
        
        // Safety check to prevent infinite loops
        if (page > 100) {
          console.warn('Reached maximum page limit, stopping pagination');
          break;
        }
      }
      
      console.log('Total products fetched:', allProducts.length);
      
      // Debug: Check if units are populated in the response
      if (allProducts.length > 0) {
        console.log('Sample product unit data:', allProducts[0].unit);
        console.log('Sample product data:', allProducts[0]);
      }
      
      setProducts(allProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      ErrorHandler.handleOperationError('fetch', error as any);
    } finally {
      setLoading(false);
    }
  };

  // Listen for header cache-only refresh to refetch products
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateProductsCache();
      fetchProducts(true);
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiService.get('/categories/all');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRootCategories = async () => {
    try {
      const response = await apiService.get('/categories/root');
      setRootCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching root categories:', error);
    }
  };

  const fetchSubCategories = async (parentId: string) => {
    try {
      const response = await apiService.get(`/categories/subcategories/${parentId}`);
      setSubCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.get('/locations/all');
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await apiService.get('/brands/all');
      console.log('Brands response:', response);
      console.log('Brands data:', response.data);
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await apiService.get('/product-types/all');
      setTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const fetchCompressorTypes = async () => {
    try {
      const response = await apiService.get('/compressor-types/all');
      setCompressorTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching compressor types:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.get('/countries/all');
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await apiService.get('/units/all');
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleCreateEntity = (type: string) => {
    setCreateModalType(type);
    setCreateModalData({ name: '', description: '', code: '', region: '', website: '' });
    setShowCreateModal(true);
  };

  const handleParentCategoryChange = async (parentId: string) => {
    setSelectedParentCategory(parentId);
    setNewProduct({ ...newProduct, category: '', unitId: newProduct.unitId, unitIds: newProduct.unitIds ?? [] }); // Reset subcategory selection, preserve unitId
    
    if (parentId) {
      await fetchSubCategories(parentId);
    } else {
      setSubCategories([]);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory(''); // Reset sub-category when category changes
  };

  const handleCreateSubmit = async () => {
    try {
      let endpoint = '';
      let data = {};

      switch (createModalType) {
        case 'brand':
          endpoint = '/brands';
          data = {
            name: createModalData.name,
            description: createModalData.description,
            website: createModalData.website,
          };
          break;
        case 'type':
          endpoint = '/product-types';
          data = {
            name: createModalData.name,
            description: createModalData.description,
          };
          break;
        case 'compressorType':
          endpoint = '/compressor-types';
          data = {
            name: createModalData.name,
            description: createModalData.description,
          };
          break;
        case 'country':
          endpoint = '/countries';
          data = {
            name: createModalData.name,
            code: createModalData.code,
            region: createModalData.region,
            description: createModalData.description,
          };
          break;
        case 'category':
          endpoint = '/categories';
          data = {
            name: createModalData.name,
            description: createModalData.description,
          };
          break;
        case 'location':
          endpoint = '/locations';
          data = {
            name: createModalData.name,
            description: createModalData.description,
          };
          break;
        case 'unit':
          endpoint = '/units';
          data = {
            name: createModalData.name,
            description: createModalData.description,
          };
          break;
        default:
          return;
      }

      await apiService.post(endpoint, data);
      toast.success(`${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)} created successfully`);
      setShowCreateModal(false);
      setCreateModalData({ name: '', description: '', website: '', code: '', region: '' });
      
      // Force refresh the appropriate data based on what was created
      switch (createModalType) {
        case 'brand':
          await fetchBrands();
          break;
        case 'type':
          await fetchTypes();
          break;
        case 'compressorType':
          await fetchCompressorTypes();
          break;
        case 'country':
          await fetchCountries();
          break;
        case 'category':
          await fetchCategories();
          await fetchRootCategories();
          break;
        case 'location':
          await fetchLocations();
          break;
        case 'unit':
          await fetchUnits();
          break;
      }

      setShowCreateModal(false);
      setCreateModalData({ name: '', description: '', code: '', region: '', website: '' });
    } catch (error) {
      console.error('Error creating entity:', error);
      ErrorHandler.handleOperationError('create', error as any);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    const product = products.find(p => p._id === productId);
    if (!product) return;

    const deleteFunction = async () => {
      try {
        const response = await apiService.delete(`/products/${productId}`);
        toast.success('Product deleted successfully');
        
        // Immediately clear products cache and force refresh
        apiService.invalidateProductsCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchProducts(true);
        }, 100);
      } catch (error) {
        console.error('Error deleting product:', error);
        ErrorHandler.handleOperationError('delete', error as any);
      }
    };

    await checkAndDelete('products', productId, product.name, deleteFunction);
  };

  const handleViewProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowViewProductModal(true);
  };

  const handleEditProduct = async (product: Product) => {
    setSelectedProduct(product);
    
    // Ensure units are loaded before proceeding
    let currentUnits = units;
    if (units.length === 0) {
      try {
        const response = await apiService.get('/units/all');
        currentUnits = response.data || [];
        setUnits(currentUnits);
      } catch (error) {
        console.error('Error fetching units:', error);
        currentUnits = [];
      }
    }
    
    // Find the unit by name to get its _id for the dropdown
    const unitObj = currentUnits.find(unit => unit.name === product.unit);
    
    // Find brand, type, compressor type, and countries by name to get their _ids for dropdowns
    const brandObj = brands.find(brand => brand.name === product.customFields?.brand);
    const typeObj = types.find(type => type.name === product.customFields?.type);
    const compressorTypeObj = compressorTypes.find(comp => comp.name === product.customFields?.compressorType);
    const countryOfOriginObj = countries.find(country => country.name === product.customFields?.countryOfOrigin);
    const countryOfAssemblyObj = countries.find(country => country.name === product.customFields?.countryOfAssembly);
    
    // Find the category and determine if it's a parent or subcategory
    const selectedCategory = product.category ? categories.find(cat => cat._id === product.category._id) : null;
    let parentCategoryId = '';
    
    if (selectedCategory) {
      if (selectedCategory.parentCategory) {
        // It's a subcategory, set the parent
        parentCategoryId = selectedCategory.parentCategory._id || selectedCategory.parentCategory;
        // Load subcategories for this parent
        fetchSubCategories(parentCategoryId);
      } else {
        // It's a root category
        parentCategoryId = selectedCategory._id;
      }
    }
    
    setSelectedParentCategory(parentCategoryId);
    
    // Map the product data to the edit form format
    const editProductData = {
      productName: product.name,
      model: product.customFields?.model || '',
      brand: brandObj ? brandObj._id : '',
      countryOfOrigin: countryOfOriginObj ? countryOfOriginObj._id : '',
      countryOfAssembly: countryOfAssemblyObj ? countryOfAssemblyObj._id : '',
      type: typeObj ? typeObj._id : '',
      compressorType: compressorTypeObj ? compressorTypeObj._id : '',
      specification: product.customFields?.specification || '',
      details: product.customFields?.details || '',
      serialNo: product.customFields?.serialNo || '',
      partNo: product.customFields?.partNo || '',
      barcodeNo: product.barcode || '',
      generatedBarcode: product.customFields?.generatedBarcode || '',
      sku: product.sku,
      price: (product.price || 0).toString(),
      costPrice: (product.costPrice || 0).toString(),
      stockQuantity: (product.stockQuantity || 0).toString(),
      minStockLevel: (product.minStockLevel || 0).toString(),
      unit: unitObj ? unitObj._id : '',
      unitIds: (product as any).units && Array.isArray((product as any).units) && (product as any).units.length > 0
        ? (product as any).units.map((name: string) => currentUnits.find((u: any) => u.name === name)?._id).filter(Boolean)
        : (unitObj ? [unitObj._id] : []),
      description: product.description || '',
      image: product.image || '',
      category: product.category?._id || '',
      location: product.location?._id || '',
      status: product.status,
    };
    
    setEditProduct(editProductData);
    setShowEditProductModal(true);
  };



  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    
    try {
      const selectedUnitIds = (editProduct as any).unitIds?.length ? (editProduct as any).unitIds : (editProduct.unit ? [editProduct.unit] : []);
      const unitNames = selectedUnitIds.map((id: string) => units.find(u => u._id === id)?.name).filter(Boolean) as string[];
      const firstUnitName = unitNames[0] || (units.find(u => u._id === editProduct.unit)?.name) || editProduct.unit;

      const brandObj = brands.find(brand => brand._id === editProduct.brand);
      const typeObj = types.find(type => type._id === editProduct.type);
      const compressorTypeObj = compressorTypes.find(comp => comp._id === editProduct.compressorType);
      const countryOfOriginObj = countries.find(country => country._id === editProduct.countryOfOrigin);
      const countryOfAssemblyObj = countries.find(country => country._id === editProduct.countryOfAssembly);

      const productData = {
        name: editProduct.productName,
        description: editProduct.description,
        category: editProduct.category,
        unit: firstUnitName,
        ...(unitNames.length > 0 && { units: unitNames }),
        price: parseFloat(editProduct.price) || 0,
        costPrice: parseFloat(editProduct.costPrice) || 0,
        stockQuantity: parseInt(editProduct.stockQuantity) || 0,
        minStockLevel: parseInt(editProduct.minStockLevel) || 0,
        location: editProduct.location,
        barcode: editProduct.barcodeNo,
        sku: editProduct.sku,
        status: editProduct.status,
        // Add custom fields for air conditioner specific data
        customFields: {
          model: editProduct.model,
          brand: brandObj ? brandObj.name : editProduct.brand,
          countryOfOrigin: countryOfOriginObj ? countryOfOriginObj.name : editProduct.countryOfOrigin,
          countryOfAssembly: countryOfAssemblyObj ? countryOfAssemblyObj.name : editProduct.countryOfAssembly,
          type: typeObj ? typeObj.name : editProduct.type,
          compressorType: compressorTypeObj ? compressorTypeObj.name : editProduct.compressorType,
          specification: editProduct.specification,
          details: editProduct.details,
          serialNo: editProduct.serialNo,
          partNo: editProduct.partNo,
          generatedBarcode: editProduct.generatedBarcode,
        },
        image: editProduct.image,
      };

      // Check for duplicate product by name and barcode (excluding current product)
      const duplicateByName = products.find(p => 
        p._id !== selectedProduct._id && 
        p.name.toLowerCase().trim() === editProduct.productName.toLowerCase().trim()
      );
      
      const duplicateByBarcode = products.find(p => 
        p._id !== selectedProduct._id && 
        p.barcode && editProduct.barcodeNo && 
        p.barcode.toLowerCase().trim() === editProduct.barcodeNo.toLowerCase().trim()
      );

      if (duplicateByName) {
        toast.error('Product already exists with the same name');
        return;
      }

      if (duplicateByBarcode) {
        toast.error('Product already exists with the same barcode');
        return;
      }

      const response = await apiService.put(`/products/${selectedProduct._id}`, productData);
      
      if (response.status >= 400) {
        const errorMsg = response.data.error || response.data.message || 'Failed to update product';
        if (response.data.errors) {
          const detailedErrors = response.data.errors.map((err: any) => err.msg).join(', ');
          toast.error(`Update failed: ${detailedErrors}`);
        } else {
          toast.error(errorMsg);
        }
        return;
      }

      toast.success('Product updated successfully');
      
      // Immediately clear products cache and force refresh
      apiService.invalidateProductsCache();
      
      setShowEditProductModal(false);
      setSelectedProduct(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchProducts(true);
      }, 100);
    } catch (error: any) {
      console.error('Error updating product:', error);
      if (error.response?.data?.error) {
        toast.error(`Failed to update product: ${error.response.data.error}`);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Failed to update product: ${errorMessages}`);
      } else {
        toast.error('Failed to update product');
      }
    }
  };

  const handleAddProduct = async () => {
    try {
      console.log('Adding new product with data:', newProduct);
      
      // Find brand, type, compressor type, and countries by _id to get their names for the backend
      const brandObj = brands.find(brand => brand._id === newProduct.brand);
      const typeObj = types.find(type => type._id === newProduct.type);
      const compressorTypeObj = compressorTypes.find(comp => comp._id === newProduct.compressorType);
      const countryOfOriginObj = countries.find(country => country._id === newProduct.countryOfOrigin);
      const countryOfAssemblyObj = countries.find(country => country._id === newProduct.countryOfAssembly);
      
      const selectedUnitIds = newProduct.unitIds?.length ? newProduct.unitIds : (newProduct.unitId ? [newProduct.unitId] : []);
      const unitNames = selectedUnitIds.map((id: string) => units.find(u => u._id === id)?.name).filter(Boolean) as string[];
      const firstUnitName = unitNames[0] || (units.find(u => u._id === newProduct.unitId)?.name) || 'pcs';

      const productData: any = {
        name: newProduct.productName,
        description: newProduct.description,
        unit: firstUnitName,
        ...(unitNames.length > 0 && { units: unitNames }),
        price: parseFloat(newProduct.price) || 0,
        costPrice: parseFloat(newProduct.costPrice) || 0,
        stockQuantity: parseInt(newProduct.stockQuantity) || 0,
        minStockLevel: Math.max(0, parseInt(newProduct.minStockLevel) || 0), // Ensure non-negative
        barcode: newProduct.barcodeNo,
        sku: newProduct.sku,
        status: newProduct.status,
        // Add custom fields for air conditioner specific data
        customFields: {
          model: newProduct.model,
          brand: brandObj ? brandObj.name : newProduct.brand,
          countryOfOrigin: countryOfOriginObj ? countryOfOriginObj.name : newProduct.countryOfOrigin,
          countryOfAssembly: countryOfAssemblyObj ? countryOfAssemblyObj.name : newProduct.countryOfAssembly,
          type: typeObj ? typeObj.name : newProduct.type,
          compressorType: compressorTypeObj ? compressorTypeObj.name : newProduct.compressorType,
          specification: newProduct.specification,
          details: newProduct.details,
          serialNo: newProduct.serialNo,
          partNo: newProduct.partNo,
          generatedBarcode: newProduct.generatedBarcode,
        }
      };

      // Handle category logic
      if (newProduct.category && newProduct.category.trim() !== '') {
        // If a sub-category is selected, use it
        productData.category = newProduct.category;
      } else if (selectedParentCategory && subCategories.length === 0) {
        // If parent category is selected but no sub-categories available, use parent category
        productData.category = selectedParentCategory;
      }
      
      if (newProduct.location && newProduct.location.trim() !== '') {
        productData.location = newProduct.location;
      }

      // Remove empty string fields to avoid validation errors
      Object.keys(productData).forEach(key => {
        if (productData[key] === "" || productData[key] === null) {
          delete productData[key];
        }
      });

      // Check for duplicate product by name and barcode
      const duplicateByName = products.find(p => 
        p.name.toLowerCase().trim() === newProduct.productName.toLowerCase().trim()
      );
      
      const duplicateByBarcode = products.find(p => 
        p.barcode && newProduct.barcodeNo && 
        p.barcode.toLowerCase().trim() === newProduct.barcodeNo.toLowerCase().trim()
      );

      if (duplicateByName) {
        toast.error('Product already exists with the same name');
        return;
      }

      if (duplicateByBarcode) {
        toast.error('Product already exists with the same barcode');
        return;
      }

      console.log('Sending product data to backend:', productData);

      const response = await apiService.post('/products', productData);
      console.log('Product creation response:', response);

      if (response.status >= 400) {
        const errorMsg = response.data.error || response.data.message || 'Failed to add product';
        if (response.data.errors) {
          const detailedErrors = response.data.errors.map((err: any) => err.msg).join(', ');
          toast.error(`Creation failed: ${detailedErrors}`);
        } else {
          toast.error(errorMsg);
        }
        return;
      }

      toast.success('Product added successfully');      
      // Immediately clear products cache and force refresh
      apiService.invalidateProductsCache();
      
      setShowAddProductModal(false);
      setNewProduct({
        productName: '',
        model: '',
        brand: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        type: '',
        compressorType: '',
        specification: '',
        details: '',
        serialNo: '',
        partNo: '',
        barcodeNo: '',
        generatedBarcode: '',
        sku: '',
        price: '',
        costPrice: '',
        stockQuantity: '',
        minStockLevel: '',
        unit: '',
        unitId: '',
        unitIds: [],
        description: '',
        image: '',
        category: '',
        location: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchProducts(true);
      }, 100);
    } catch (error: any) {
      console.error('Error adding product:', error);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.data?.errors) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Display single error message
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add product');
      }
    }
  };

  const filteredProducts = products.filter(product => {
    // Enhanced search with comprehensive field matching
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      // Basic fields
      product.name.toLowerCase().includes(searchLower) ||
      product.sku.toLowerCase().includes(searchLower) ||
      product.barcode?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower) ||
      product.unit.toLowerCase().includes(searchLower) ||
      product.status.toLowerCase().includes(searchLower) ||
      product.price?.toString().includes(searchLower) ||
      product.costPrice?.toString().includes(searchLower) ||
      product.stockQuantity?.toString().includes(searchLower) ||
      product.minStockLevel?.toString().includes(searchLower) ||
      
      // Custom fields
      product.customFields?.brand?.toLowerCase().includes(searchLower) ||
      product.customFields?.model?.toLowerCase().includes(searchLower) ||
      product.customFields?.type?.toLowerCase().includes(searchLower) ||
      product.customFields?.compressorType?.toLowerCase().includes(searchLower) ||
      product.customFields?.specification?.toLowerCase().includes(searchLower) ||
      product.customFields?.details?.toLowerCase().includes(searchLower) ||
      product.customFields?.serialNo?.toLowerCase().includes(searchLower) ||
      product.customFields?.partNo?.toLowerCase().includes(searchLower) ||
      product.customFields?.capacity?.toLowerCase().includes(searchLower) ||
      product.customFields?.countryOfOrigin?.toLowerCase().includes(searchLower) ||
      product.customFields?.countryOfAssembly?.toLowerCase().includes(searchLower) ||
      product.customFields?.description?.toLowerCase().includes(searchLower) ||
      product.customFields?.notes?.toLowerCase().includes(searchLower) ||
      product.customFields?.comments?.toLowerCase().includes(searchLower) ||
      
      // Related entity fields
      (typeof product.category === 'object' && product.category?.name?.toLowerCase().includes(searchLower)) ||
      (typeof product.location === 'object' && product.location?.name?.toLowerCase().includes(searchLower)) ||
      (typeof product.supplier === 'object' && product.supplier?.name?.toLowerCase().includes(searchLower)) ||
      (typeof product.supplier === 'string' && product.supplier?.toLowerCase().includes(searchLower));
    
    // Category filtering logic
    let matchesCategory = true;
    if (selectedCategory || selectedSubCategory) {
      // Check if product.category exists before accessing its properties
      if (!product.category) {
        matchesCategory = false; // If no category is set, it doesn't match any category filter
      } else if (selectedSubCategory) {
        // If sub-category is selected, filter by sub-category
        matchesCategory = product.category._id === selectedSubCategory;
      } else if (selectedCategory) {
        // If only category is selected, filter by products that belong to sub-categories of this category
        const subCategoriesOfSelectedCategory = categories.filter(cat => {
          const parentIdStr = (cat.parentCategory as any)?._id || (cat.parentCategory as any)?.toString() || '';
          const selectedIdStr = selectedCategory;
          return parentIdStr === selectedIdStr;
        }).map(cat => cat._id);
        
        matchesCategory = subCategoriesOfSelectedCategory.includes(product.category._id);
      }
    }
    
    const matchesLocation = !selectedLocation || (product.location && product.location._id === selectedLocation);
    const matchesBrand = !selectedBrand || product.customFields?.brand === selectedBrand;
    const matchesStatus = !statusFilter || product.status === statusFilter;
    
    // Add debugging for product filtering (simplified to avoid React rendering issues)
    console.log('Product filtering:', {
      productName: product.name,
      productCategoryId: product.category?._id || 'null',
      productBrand: product.customFields?.brand || '',
      selectedCategory: selectedCategory || '',
      selectedSubCategory: selectedSubCategory || '',
      selectedBrand: selectedBrand || '',
      matchesCategory,
      matchesLocation,
      matchesBrand,
      matchesStatus,
      finalResult: matchesSearch && matchesCategory && matchesLocation && matchesBrand && matchesStatus
    });
    
    return matchesSearch && matchesCategory && matchesLocation && matchesBrand && matchesStatus;
  });

  const exportProducts = async () => {
    try {
      await apiService.download('/products/export/excel', 'products.xlsx');
      toast.success('Products exported successfully');
    } catch (error) {
      console.error('Error exporting products:', error);
      toast.error('Failed to export products');
    }
  };



  const getStockStatus = (quantity: number, minLevel: number) => {
    if (quantity === 0) return { status: 'out-of-stock', color: 'text-[var(--color-red-600)]', bg: 'bg-[var(--color-red-100)]' };
    if (quantity <= minLevel) return { status: 'low-stock', color: 'text-[var(--color-yellow-600)]', bg: 'bg-[var(--color-yellow-100)]' };
    return { status: 'in-stock', color: 'text-[var(--color-green-600)]', bg: 'bg-[var(--color-green-100)]' };
  };

  // Define isAirConditioner for different contexts
  const airconCategories = ['Air-Conditioner', 'VRF', 'Chiller', 'Split Type'];
  
  // For Add Product and Edit Product: Check parent category for conditional fields
  const selectedParentCategoryObj = rootCategories.find(c => c._id === selectedParentCategory);
  const isAirConditioner = selectedParentCategoryObj ? airconCategories.includes(selectedParentCategoryObj.name) : false;
  const isEditAirConditioner = isAirConditioner; // Reuse the same logic since both modals use selectedParentCategory
  
  // Helper to check if a category is an air conditioner category (including parent check)
  const isCategoryAircon = (categoryId?: string) => {
    if (!categoryId) return false;
    const cat = categories.find(c => c._id === categoryId);
    if (!cat) return false;
    if (airconCategories.includes(cat.name)) return true;
    const parentId = typeof cat.parentCategory === 'object' ? cat.parentCategory?._id : cat.parentCategory;
    if (!parentId) return false;
    const parentCat = rootCategories.find(c => c._id === parentId) || categories.find(c => c._id === parentId);
    return parentCat ? airconCategories.includes(parentCat.name) : false;
  };

  // For View Product: Check the product's category or its parent
  const isViewAirConditioner = isCategoryAircon(selectedProduct?.category?._id);

  // Add useEffect to generate barcode image for view modal
  useEffect(() => {
    if (showViewProductModal && (selectedProduct?.barcode || selectedProduct?.customFields?.generatedBarcode)) {
      const barcodeText = selectedProduct?.barcode || selectedProduct?.customFields?.generatedBarcode || '';
      if (barcodeText && viewBarcodeCanvasRef.current) {
        JsBarcode(viewBarcodeCanvasRef.current, barcodeText, {
          format: "CODE128",
          width: 2,
          height: 80,
          displayValue: true,
          fontSize: 14,
          margin: 8,
          background: "#ffffff",
          lineColor: "#000000"
        });
        setViewBarcodeImage(viewBarcodeCanvasRef.current.toDataURL("image/png"));
      } else {
        setViewBarcodeImage(null);
      }
    } else {
      setViewBarcodeImage(null);
    }
  }, [showViewProductModal, selectedProduct]);

  // Import handler
  const handleExcelImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateFile(file)) return;
    setImportFile(file); // Store the file for upload
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      try {
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        setImportedProducts(data);
      } catch (err) {
        setImportError('Failed to parse Excel file. Please check the format.');
        setImportedProducts([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImportConfirm = async () => {
    setImportLoading(true);
    setImportError(null);
    try {
      if (!importFile) {
        setImportError('No file selected');
        setImportLoading(false);
        return;
      }
      // Send the file as FormData to backend
      const formData = new FormData();
      formData.append('file', importFile);
      await apiService.importExcel('/products/import/excel', formData);
      toast.success('Products imported successfully');
      setShowImportModal(false);
      setImportedProducts([]);
      setImportFile(null);
    } catch (error) {
      console.error('Error importing products:', error);
      setImportError('Failed to import products');
    } finally {
      setImportLoading(false);
    }
  };

  // Add debug log when headerError or showHeaderErrorModal changes
  useEffect(() => {
    if (showHeaderErrorModal && headerError) {
      // eslint-disable-next-line no-console
      console.log('Header Error State:', headerError);
    }
  }, [showHeaderErrorModal, headerError]);

  if (loading) {
    console.log('ProductsPage is loading, showing spinner');
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  console.log('ProductsPage rendering, loading:', loading);
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-['Times_New_Roman']">Products</h1>
          <p className="text-[var(--color-text)] font-['Times_New_Roman']">
            Manage your inventory products and stock levels
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateProductsCache(); 
              fetchProducts(true); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors bg-transparent text-[var(--color-text)]"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={exportProducts}
            className="flex items-center px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <Button variant="primary" onClick={() => setShowAddProductModal(true)} icon={<Plus className="w-4 h-4 mr-2" />}>Add Product</Button>
          <Button variant="primary" onClick={() => setShowImportModal(true)}>Import from Excel</Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-4 h-4" />
              <input
                type="text"
                placeholder="Search products by name, SKU, barcode, brand, model, type, specification, serial no, part no, description, etc..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  // Update URL with search parameter
                  if (value) {
                    setSearchParams({ search: value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                style={{ borderRadius: 'var(--border-radius)', background: 'var(--color-surface)', color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors"
            style={{ borderRadius: 'var(--border-radius)', fontFamily: 'var(--font-family)' } as any}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              <option value="">All Categories</option>
              {categories.filter(cat => !cat.parentCategory).map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
              disabled={!selectedCategory}
            >
              <option value="">All Sub Categories</option>
              {categories.filter(cat => {
                // Handle MongoDB ObjectId comparison
                const parentId = cat.parentCategory;
                const selectedId = selectedCategory;
                
                // Convert both to strings for comparison
                const parentIdStr = parentId ? (typeof parentId === 'object' ? parentId._id || parentId.toString() : parentId.toString()) : null;
                const selectedIdStr = selectedId;
                
                console.log('Sub category filter:', {
                  categoryName: cat.name,
                  parentId: parentId,
                  parentIdType: typeof parentId,
                  selectedId: selectedId,
                  selectedIdType: typeof selectedId,
                  parentIdStr: parentIdStr,
                  matches: parentIdStr === selectedIdStr
                });
                
                return parentIdStr === selectedIdStr;
              }).map(subCategory => (
                <option key={subCategory._id} value={subCategory._id}>
                  {subCategory.name}
                </option>
              ))}
            </select>

            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              <option value="">All Locations</option>
              {locations.map(location => (
                <option key={location._id} value={location._id}>
                  {location.name}
                </option>
              ))}
            </select>

            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              <option value="">All Brands</option>
              {brands.map(brand => (
                <option key={brand._id} value={brand.name}>
                  {brand.name}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </motion.div>
        )}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => {
          const stockStatus = getStockStatus(product.stockQuantity, product.minStockLevel);
          
          return (
            <motion.div
              key={product._id}
              initial={{ opacity: 0, y: 20 } as any}
              animate={{ opacity: 1, y: 0 } as any}
              className="bg-[var(--color-surface)] rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              {/* Product Image */}
              <div className="h-32 bg-[var(--color-background)] flex items-center justify-center">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-12 h-12 text-[var(--color-text-secondary)]" />
                )}
              </div>

              {/* Product Info */}
              <div className="p-3 space-y-2">
                <div>
                  <h3 className="font-semibold text-[var(--color-text)] font-['Times_New_Roman'] line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    SKU: {product.sku}
                  </p>
                  {/* Additional Product Info */}
                  <div className="mt-1 space-y-0.5">
                    {product.customFields?.brand && (
                      <p className="text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']"><span className="font-semibold">Brand:</span> {product.customFields.brand}</p>
                    )}
                    {product.customFields?.model && (
                      <p className="text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']"><span className="font-semibold">Model:</span> {product.customFields.model}</p>
                    )}
                    {product.customFields?.type && (
                      <p className="text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']"><span className="font-semibold">Type:</span> {product.customFields.type}</p>
                    )}
                    {product.customFields?.compressorType && (
                      <p className="text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']"><span className="font-semibold">Compressor Type:</span> {product.customFields.compressorType}</p>
                    )}
                  </div>
                </div>



                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    <Tag className="w-4 h-4 mr-1" />
                    {typeof product.category === 'object' && product.category !== null 
                      ? product.category.name || 'No Category'
                      : product.category || 'No Category'
                    }
                  </div>
                  <div className="flex items-center text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    <MapPin className="w-4 h-4 mr-1" />
                    {typeof product.location === 'object' && product.location !== null 
                      ? product.location.name || 'No Location'
                      : product.location || 'No Location'
                    }
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center mt-2">
                    <span className="text-xl font-bold text-[var(--color-primary)] font-['Times_New_Roman']">
                      {currencySymbol}{product.price?.toLocaleString('en-BD') ?? '0'}
                    </span>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${stockStatus.bg} ${stockStatus.color} font-['Times_New_Roman']`}>
                    {product.stockQuantity} {product.unit}
                  </div>
                </div>

                {stockStatus.status === 'low-stock' && (
                  <div className="flex items-center text-xs text-[var(--color-yellow-600)] font-['Times_New_Roman']">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Low stock alert
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-[var(--color-border)]">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewProduct(product)}
                      className="p-2 text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="p-2 text-[var(--color-green-600)] hover:bg-[var(--color-green-light)] rounded-lg transition-colors"
                      title="Edit Product"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteProduct(product._id)}
                    className="p-2 text-[var(--color-red)] hover:bg-[var(--color-red-light)] rounded-lg transition-colors"
                    title="Delete Product"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
            No products found
          </h3>
          <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
            {searchTerm || selectedCategory || selectedLocation || statusFilter
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first product'
            }
          </p>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
        Showing {filteredProducts.length} of {products.length} products
      </div>

      <Modal isOpen={showAddProductModal} onClose={() => {
        setShowAddProductModal(false);
        setSelectedParentCategory('');
        setSubCategories([]);
      }} title="Add Product" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Location</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.location} onChange={e => setNewProduct({ ...newProduct, location: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Location</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id}>{location.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('location')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Location" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Parent Category</label>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedParentCategory} 
                onChange={e => handleParentCategoryChange(e.target.value)} 
                className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                style={{ borderRadius: 'var(--border-radius)' } as any}
              >
                <option value="">Select Parent Category</option>
                {rootCategories.map(category => (
                  <option key={category._id} value={category._id}>{category.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('category')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Category" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {selectedParentCategory && subCategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Sub Category</label>
              <div className="flex items-center space-x-2">
                <select 
                  value={newProduct.category} 
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} 
                  className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories.map(category => (
                    <option key={category._id} value={category._id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <Input label="Product Name" value={newProduct.productName} onChange={e => setNewProduct({ ...newProduct, productName: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Brand</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.brand} onChange={e => updateBarcode({ ...newProduct, brand: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('brand')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Brand" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <Input label="Model" value={newProduct.model} onChange={e => updateBarcode({ ...newProduct, model: e.target.value })} />
          <Input label="SKU" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.type} onChange={e => updateBarcode({ ...newProduct, type: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Type</option>
                  {types.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('type')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Compressor Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.compressorType} onChange={e => updateBarcode({ ...newProduct, compressorType: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Compressor Type</option>
                  {compressorTypes.map(compressorType => (
                    <option key={compressorType._id} value={compressorType._id}>{compressorType.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('compressorType')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Compressor Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Origin</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.countryOfOrigin} onChange={e => setNewProduct({ ...newProduct, countryOfOrigin: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Country of Origin</option>
                {countries.map(country => (
                  <option key={country._id} value={country._id}>{country.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Origin" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Assembly</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.countryOfAssembly} onChange={e => setNewProduct({ ...newProduct, countryOfAssembly: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Country of Assembly</option>
                  {countries.map(country => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Assembly" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {!isAirConditioner && <Input label="Specifications" value={newProduct.specification} onChange={e => setNewProduct({ ...newProduct, specification: e.target.value })} />}
          {!isAirConditioner && <Input label="Serial No." value={newProduct.serialNo} onChange={e => setNewProduct({ ...newProduct, serialNo: e.target.value })} />}
          {!isAirConditioner && <Input label="Part No." value={newProduct.partNo} onChange={e => setNewProduct({ ...newProduct, partNo: e.target.value })} />}
          <Input label="Details" value={newProduct.details} onChange={e => setNewProduct({ ...newProduct, details: e.target.value })} />
          <Input label="Description" value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
          <Input label="Price" type="number" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} />
          <Input label="Cost Price" type="number" value={newProduct.costPrice} onChange={e => setNewProduct({ ...newProduct, costPrice: e.target.value })} />
          <Input label="Stock Quantity" type="number" value={newProduct.stockQuantity} onChange={e => setNewProduct({ ...newProduct, stockQuantity: e.target.value })} />
          <Input label="Min Stock Level" type="number" value={newProduct.minStockLevel} onChange={e => setNewProduct({ ...newProduct, minStockLevel: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Units</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1" ref={addUnitsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setAddProductUnitsDropdownOpen(prev => !prev)}
                  className="input w-full flex items-center justify-between dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)] text-left"
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <span>
                    {newProduct.unitIds?.length
                      ? `${newProduct.unitIds.length} unit(s) selected`
                      : 'Select units'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${addProductUnitsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {addProductUnitsDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full border border-[var(--color-border)] rounded-lg shadow-lg bg-[var(--color-surface)] dark:bg-[var(--color-surface)] max-h-48 overflow-auto" style={{ borderRadius: 'var(--border-radius)' } as any}>
                    {units.filter(u => u.isActive !== false).map(unit => (
                      <label key={unit._id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--color-surface-light)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.unitIds?.includes(unit._id) ?? false}
                          onChange={() => {
                            const current = newProduct.unitIds || [];
                            const next = current.includes(unit._id)
                              ? current.filter(id => id !== unit._id)
                              : [...current, unit._id];
                            setNewProduct({ ...newProduct, unitIds: next, unitId: next[0] || newProduct.unitId });
                          }}
                          className="rounded border-[var(--color-border)]"
                        />
                        <span className="text-[var(--color-text)] font-['Times_New_Roman']">{unit.name}</span>
                      </label>
                    ))}
                    {units.filter(u => u.isActive !== false).length === 0 && (
                      <div className="px-3 py-2 text-[var(--color-text-secondary)] text-sm">No units available</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => handleCreateEntity('unit')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded shrink-0" title="Create Unit" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {/* Hidden canvas for barcode generation */}
          <canvas 
            ref={barcodeCanvasRef} 
            style={{ display: 'none' } as any}
            width="400" 
            height="150"
          />
          
          {/* Barcode Image Display */}
          {barcodeImage && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Generated Barcode Image</label>
              <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <img 
                  src={barcodeImage} 
                  alt="Generated Barcode" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '120px' } as any}
                />
                <div className="text-center mt-2">
                  <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman'] mb-2">{newProduct.generatedBarcode}</p>
                  <button
                    type="button"
                    onClick={downloadBarcodeImage}
                    className="px-3 py-1 bg-[var(--color-green)] text-[var(--color-white)] rounded text-sm hover:bg-[var(--color-green-dark)] transition-colors"
                  >
                    Download Barcode
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <Input label="Image URL" value={newProduct.image} onChange={e => setNewProduct({ ...newProduct, image: e.target.value })} />
          <button className="btn btn-primary w-full" onClick={handleAddProduct}>Add Product</button>
        </div>
      </Modal>

      {/* Create Entity Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={`Create ${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)}`} size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input 
            label="Name" 
            value={createModalData.name} 
            onChange={e => setCreateModalData({ ...createModalData, name: e.target.value })} 
            required 
          />
          {(createModalType === 'brand' || createModalType === 'category' || createModalType === 'type' || createModalType === 'compressorType' || createModalType === 'location' || createModalType === 'country' || createModalType === 'unit') && (
            <Input 
              label="Description" 
              value={createModalData.description} 
              onChange={e => setCreateModalData({ ...createModalData, description: e.target.value })} 
            />
          )}
          {createModalType === 'brand' && (
            <Input 
              label="Website" 
              value={createModalData.website} 
              onChange={e => setCreateModalData({ ...createModalData, website: e.target.value })} 
            />
          )}
          {createModalType === 'country' && (
            <>
              <Input 
                label="Code" 
                value={createModalData.code} 
                onChange={e => setCreateModalData({ ...createModalData, code: e.target.value })} 
              />
              <Input 
                label="Region" 
                value={createModalData.region} 
                onChange={e => setCreateModalData({ ...createModalData, region: e.target.value })} 
              />
            </>
          )}
          <div className="flex space-x-3">
            <button 
              className="btn btn-secondary flex-1" 
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary flex-1" 
              onClick={handleCreateSubmit}
              disabled={!createModalData.name.trim()}
            >
              Create
            </button>
          </div>
        </div>
      </Modal>

      {/* View Product Modal */}
      <Modal isOpen={showViewProductModal} onClose={() => setShowViewProductModal(false)} title="Product Details" size="xl" className="!max-w-7xl">
        {selectedProduct && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Product Name</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">SKU</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.sku}</p>
              </div>
                              <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Category</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">
                    {typeof selectedProduct.category === 'object' && selectedProduct.category !== null 
                      ? selectedProduct.category.name || 'No Category'
                      : selectedProduct.category || 'No Category'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Location</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">
                    {typeof selectedProduct.location === 'object' && selectedProduct.location !== null 
                      ? selectedProduct.location.name || 'No Location'
                      : selectedProduct.location || 'No Location'
                    }
                  </p>
                </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Price</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">
                  {currencySymbol}{selectedProduct.price?.toLocaleString() || '0'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Cost Price</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">
                  {currencySymbol}{selectedProduct.costPrice?.toLocaleString() || '0'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Stock Quantity</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.stockQuantity || 0} {selectedProduct.unit || 'pcs'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Min Stock Level</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.minStockLevel || 0} {selectedProduct.unit || 'pcs'}</p>
              </div>
              {selectedProduct.barcode && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Barcode</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.barcode}</p>
                </div>
              )}
              {selectedProduct.customFields?.generatedBarcode && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Generated Barcode</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.generatedBarcode}</p>
                </div>
              )}
              {selectedProduct.customFields?.model && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Model</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.model}</p>
                </div>
              )}
              {selectedProduct.customFields?.brand && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Brand</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.brand}</p>
                </div>
              )}
              {selectedProduct.customFields?.type && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Type</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.type}</p>
                </div>
              )}
              {selectedProduct.customFields?.compressorType && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Compressor Type</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.compressorType}</p>
                </div>
              )}
              {selectedProduct.customFields?.countryOfOrigin && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Origin</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.countryOfOrigin}</p>
                </div>
              )}
              {selectedProduct.customFields?.countryOfAssembly && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Assembly</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.countryOfAssembly}</p>
                </div>
              )}
              {!isViewAirConditioner && selectedProduct.customFields?.specification && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Specifications</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.specification}</p>
                </div>
              )}
              {!isViewAirConditioner && selectedProduct.customFields?.serialNo && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Serial No.</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.serialNo}</p>
                </div>
              )}
              {!isViewAirConditioner && selectedProduct.customFields?.partNo && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Part No.</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.partNo}</p>
                </div>
              )}
              {selectedProduct.supplier && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Supplier</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{typeof selectedProduct.supplier === 'object' && selectedProduct.supplier !== null ? selectedProduct.supplier.name : selectedProduct.supplier}</p>
                </div>
              )}
              {selectedProduct.status && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Status</label>
                  <p className={`font-['Times_New_Roman'] ${selectedProduct.status === 'active' ? 'text-[var(--color-green-600)]' : 'text-[var(--color-red-600)]'}`}>
                    {selectedProduct.status.charAt(0).toUpperCase() + selectedProduct.status.slice(1)}
                  </p>
                </div>
              )}
              {selectedProduct.createdAt && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Created At</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{formatDateTime(selectedProduct.createdAt, settings)}</p>
                </div>
              )}
              {selectedProduct.updatedAt && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Last Updated</label>
                  <p className="text-[var(--color-text)] font-['Times_New_Roman']">{formatDateTime(selectedProduct.updatedAt, settings)}</p>
                </div>
              )}
            </div>
            {selectedProduct.description && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Description</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.description}</p>
              </div>
            )}
            {selectedProduct.customFields?.details && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Details</label>
                <p className="text-[var(--color-text)] font-['Times_New_Roman']">{selectedProduct.customFields?.details}</p>
              </div>
            )}
            
            {/* Hidden canvas for barcode generation in view modal */}
            <canvas 
              ref={viewBarcodeCanvasRef} 
              style={{ display: 'none' } as unknown as React.CSSProperties} 
              width="400" 
              height="120"
            />
            
            {/* Barcode Image Display for existing products */}
            {(selectedProduct.barcode || selectedProduct.customFields?.generatedBarcode) && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Barcode Image</label>
                <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  {viewBarcodeImage ? (
                    <img 
                      src={viewBarcodeImage} 
                      alt="Product Barcode" 
                      className="max-w-full h-auto mx-auto"
                      style={{ maxHeight: '120px' } as any}
                    />
                  ) : (
                    <div className="text-center py-4 text-[var(--color-text-secondary)]">
                      Unable to generate barcode image
                    </div>
                  )}
                  <div className="text-center mt-2">
                    <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman'] mb-2">
                      {selectedProduct.barcode || selectedProduct.customFields?.generatedBarcode}
                    </p>
                    {viewBarcodeImage && (
                      <button
                        type="button"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.download = `barcode-${selectedProduct.barcode || selectedProduct.customFields?.generatedBarcode}.png`;
                          link.href = viewBarcodeImage;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="px-3 py-1 bg-[var(--color-green)] text-[var(--color-white)] rounded text-sm hover:bg-[var(--color-green-dark)] transition-colors"
                      >
                        Download Barcode
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowViewProductModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Product Modal */}
      <Modal isOpen={showEditProductModal} onClose={() => {
        setShowEditProductModal(false);
        setSelectedParentCategory('');
        setSubCategories([]);
      }} title="Edit Product" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Location</label>
            <div className="flex items-center space-x-2">
              <select value={editProduct.location} onChange={e => setEditProduct({ ...editProduct, location: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Location</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id}>{location.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('location')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Location" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Parent Category</label>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedParentCategory} 
                onChange={e => handleParentCategoryChange(e.target.value)} 
                className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                style={{ borderRadius: 'var(--border-radius)' } as any}
              >
                <option value="">Select Parent Category</option>
                {rootCategories.map(category => (
                  <option key={category._id} value={category._id}>{category.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('category')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Category" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {selectedParentCategory && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Sub Category</label>
              <div className="flex items-center space-x-2">
                <select 
                  value={editProduct.category} 
                  onChange={e => setEditProduct({ ...editProduct, category: e.target.value })} 
                  className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories.map(category => (
                    <option key={category._id} value={category._id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <Input label="Product Name" value={editProduct.productName} onChange={e => setEditProduct({ ...editProduct, productName: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Brand</label>
            <div className="flex items-center space-x-2">
              <select value={editProduct.brand} onChange={e => updateEditBarcode({ ...editProduct, brand: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('brand')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Brand" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <Input label="Model" value={editProduct.model} onChange={e => updateEditBarcode({ ...editProduct, model: e.target.value })} />
          <Input label="SKU" value={editProduct.sku} onChange={e => setEditProduct({ ...editProduct, sku: e.target.value })} />
          {isEditAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Type</label>
              <div className="flex items-center space-x-2">
                <select value={editProduct.type} onChange={e => updateEditBarcode({ ...editProduct, type: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Type</option>
                  {types.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('type')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {isEditAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Compressor Type</label>
              <div className="flex items-center space-x-2">
                <select value={editProduct.compressorType} onChange={e => updateEditBarcode({ ...editProduct, compressorType: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Compressor Type</option>
                  {compressorTypes.map(compressorType => (
                    <option key={compressorType._id} value={compressorType._id}>{compressorType.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('compressorType')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Compressor Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          <Input label="Image URL" value={editProduct.image} onChange={e => setEditProduct({ ...editProduct, image: e.target.value })} />
          {/* Add missing fields to Edit Product modal */}
          {isEditAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Origin</label>
              <div className="flex items-center space-x-2">
                <select value={editProduct.countryOfOrigin} onChange={e => setEditProduct({ ...editProduct, countryOfOrigin: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Country of Origin</option>
                  {countries.map(country => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Origin" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
                </div>
            </div>
          )}
          {isEditAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Assembly</label>
              <div className="flex items-center space-x-2">
                <select value={editProduct.countryOfAssembly} onChange={e => setEditProduct({ ...editProduct, countryOfAssembly: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Country of Assembly</option>
                  {countries.map(country => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Assembly" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {!isEditAirConditioner && <Input label="Specifications" value={editProduct.specification} onChange={e => setEditProduct({ ...editProduct, specification: e.target.value })} />}
          {!isEditAirConditioner && <Input label="Serial No." value={editProduct.serialNo} onChange={e => setEditProduct({ ...editProduct, serialNo: e.target.value })} />}
          {!isEditAirConditioner && <Input label="Part No." value={editProduct.partNo} onChange={e => setEditProduct({ ...editProduct, partNo: e.target.value })} />}
          <Input label="Details" value={editProduct.details} onChange={e => setEditProduct({ ...editProduct, details: e.target.value })} />
          <Input label="Description" value={editProduct.description} onChange={e => setEditProduct({ ...editProduct, description: e.target.value })} />
          <Input label="Price" type="number" value={editProduct.price} onChange={e => setEditProduct({ ...editProduct, price: e.target.value })} />
          <Input label="Cost Price" type="number" value={editProduct.costPrice} onChange={e => setEditProduct({ ...editProduct, costPrice: e.target.value })} />
          <Input label="Stock Quantity" type="number" value={editProduct.stockQuantity} onChange={e => setEditProduct({ ...editProduct, stockQuantity: e.target.value })} />
          <Input label="Min Stock Level" type="number" value={editProduct.minStockLevel} onChange={e => setEditProduct({ ...editProduct, minStockLevel: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Units</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1" ref={editUnitsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setEditProductUnitsDropdownOpen(prev => !prev)}
                  className="input w-full flex items-center justify-between dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)] text-left"
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <span>
                    {(editProduct as any).unitIds?.length
                      ? `${(editProduct as any).unitIds.length} unit(s) selected`
                      : 'Select units'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${editProductUnitsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {editProductUnitsDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full border border-[var(--color-border)] rounded-lg shadow-lg bg-[var(--color-surface)] dark:bg-[var(--color-surface)] max-h-48 overflow-auto" style={{ borderRadius: 'var(--border-radius)' } as any}>
                    {units.filter((u: any) => u.isActive !== false).map((unit: any) => (
                      <label key={unit._id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--color-surface-light)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={((editProduct as any).unitIds || []).includes(unit._id)}
                          onChange={() => {
                            const current = (editProduct as any).unitIds || [];
                            const next = current.includes(unit._id)
                              ? current.filter((id: string) => id !== unit._id)
                              : [...current, unit._id];
                            setEditProduct({ ...editProduct, unitIds: next, unit: next[0] || editProduct.unit });
                          }}
                          className="rounded border-[var(--color-border)]"
                        />
                        <span className="text-[var(--color-text)] font-['Times_New_Roman']">{unit.name}</span>
                      </label>
                    ))}
                    {units.filter((u: any) => u.isActive !== false).length === 0 && (
                      <div className="px-3 py-2 text-[var(--color-text-secondary)] text-sm">No units available</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => handleCreateEntity('unit')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded shrink-0" title="Create Unit" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <div className="flex space-x-3">
            <button 
              className="btn btn-secondary flex-1" 
              onClick={() => setShowEditProductModal(false)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary flex-1" 
              onClick={handleUpdateProduct}
            >
              Update Product
            </button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)} title="Import Products from Excel" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelImport}
            className="block w-full text-sm text-gray-700"
          />
          {importError && <div className="text-red-600 text-sm">{importError}</div>}
          {importedProducts.length > 0 && (
            <div className="max-h-64 overflow-auto border border-[var(--color-border)] rounded p-2 bg-[var(--color-background)]">
              <table className="min-w-full text-xs text-[var(--color-text)]">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    {Object.keys(importedProducts[0]).map((key) => (
                      <th key={key} className="px-2 py-1 text-left font-bold text-[var(--color-text-secondary)]">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {importedProducts.slice(0, 10).map((row, idx) => (
                    <tr key={idx}>
                      {Object.keys(importedProducts[0]).map((key) => (
                        <td key={key} className="px-2 py-1">{row[key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {importedProducts.length > 10 && <div className="text-xs text-[var(--color-text-secondary)] mt-2">Showing first 10 rows of {importedProducts.length}</div>}
            </div>
          )}
          <div className="flex space-x-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(false)}
              disabled={importLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleImportConfirm}
              disabled={importedProducts.length === 0 || importLoading}
            >
              {importLoading ? 'Importing...' : 'Import'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Errors Modal */}
      <Modal isOpen={showImportErrorsModal} onClose={() => setShowImportErrorsModal(false)} size="xl" className="max-w-2xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-red-600">Import Errors</h2>
          <p className="mb-2">Some rows could not be imported. Please fix the errors and try again.</p>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">Row</th>
                  <th className="px-4 py-2 text-left">Error</th>
                </tr>
              </thead>
              <tbody>
                {importErrors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2">{err.row}</td>
                    <td className="px-4 py-2 text-red-700">{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-right">
            <button onClick={() => setShowImportErrorsModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded">Close</button>
          </div>
        </div>
      </Modal>

      {/* Header Error Modal */}
      <Modal isOpen={showHeaderErrorModal} onClose={() => setShowHeaderErrorModal(false)} size="md" className="max-w-lg">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-red-600">Header Mismatch</h2>
          <p className="mb-2">The Excel file headers do not match the required columns. Please fix the following issues and try again:</p>
          {headerError && (
            <>
              {headerError.missing && headerError.missing.length > 0 && (
                <div className="mb-2">
                  <span className="font-semibold text-red-700">Missing columns:</span>
                  <ul className="list-disc ml-6 text-red-700">
                    {headerError.missing.map((h, idx) => <li key={idx}>{h}</li>)}
                  </ul>
                </div>
              )}
              {headerError.extra && headerError.extra.length > 0 && (
                <div className="mb-2">
                  <span className="font-semibold text-red-700">Extra columns:</span>
                  <ul className="list-disc ml-6 text-red-700">
                    {headerError.extra.map((h, idx) => <li key={idx}>{h}</li>)}
                  </ul>
                </div>
              )}
              {(!headerError.missing || headerError.missing.length === 0) && (!headerError.extra || headerError.extra.length === 0) && (
                <div className="mb-2 text-gray-500">No header issues detected (debug fallback).</div>
              )}
            </>
          )}
          <div className="mt-4 text-right">
            <button onClick={() => setShowHeaderErrorModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded">Close</button>
          </div>
        </div>
      </Modal>

      {/* Deletion Protection Modal */}
      <DeletionProtectionModal
        isOpen={showDeletionProtectionModal}
        onClose={closeDeletionProtectionModal}
        entityName={deletionProtectionData?.entityName || ''}
        entityType={deletionProtectionData?.entityType || ''}
        dependencies={deletionProtectionData?.dependencies || []}
        onViewDetails={(dependency) => {
          // Navigate to the dependency details
          console.log('View details for:', dependency);
          // You can implement navigation here if needed
        }}
      />
    </div>
  );
};

export default ProductsPage;